import { useRef, useState, type FormEvent } from 'react'
import type { AccountTrip, AccountTripUpdate } from '../../api'
import './trip-dialog.css'

export function shareUrlForTrip(trip: AccountTrip, baseUrl = 'https://www.mapapal.com/'): string | null {
  if (!trip.shareToken) return null
  const url = new URL(baseUrl)
  url.searchParams.set('id', trip.id)
  url.searchParams.set('uno', trip.shareToken)
  return url.toString()
}

export function ShareTripDialog({ trip, onClose }: { trip: AccountTrip; onClose: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [copied, setCopied] = useState(false)
  const shareUrl = shareUrlForTrip(trip)

  const copy = async () => {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
    } catch {
      inputRef.current?.select()
      document.execCommand('copy')
    }
    setCopied(true)
  }

  const share = async () => {
    if (!shareUrl || !navigator.share) return
    try {
      await navigator.share({
        title: trip.description || 'Mapapal trip',
        text: `Follow ${trip.ownerName}'s trip on Mapapal.`,
        url: shareUrl,
      })
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) setCopied(false)
    }
  }

  return (
    <div className="settings-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section className="settings-dialog trip-dialog" role="dialog" aria-modal="true" aria-labelledby="share-trip-title">
        <div className="settings-dialog__heading">
          <div><p>Your trip</p><h2 id="share-trip-title">Share trip</h2></div>
          <button type="button" aria-label="Close share trip" onClick={onClose}>×</button>
        </div>
        {shareUrl ? (
          <>
            <p className="trip-dialog__intro">Anyone with this private link can open the trip.</p>
            <label className="trip-dialog__field">
              <span>Share link</span>
              <input ref={inputRef} type="url" readOnly value={shareUrl} onFocus={(event) => event.currentTarget.select()} />
            </label>
            <div className="trip-dialog__buttons">
              <button className="trip-dialog__primary" type="button" onClick={copy}>{copied ? 'Copied!' : 'Copy link'}</button>
              {'share' in navigator ? <button className="trip-dialog__secondary" type="button" onClick={share}>Share…</button> : null}
            </div>
          </>
        ) : (
          <p className="trip-dialog__message" role="status">A private share link is not available for this trip yet.</p>
        )}
      </section>
    </div>
  )
}

export function EditTripDialog({
  trip,
  onSave,
  onEnd,
  onDelete,
  onClose,
}: {
  trip: AccountTrip
  onSave: (update: AccountTripUpdate) => Promise<void>
  onEnd?: () => Promise<void>
  onDelete: () => Promise<void>
  onClose: () => void
}) {
  const [description, setDescription] = useState(trip.description ?? '')
  const [visibility, setVisibility] = useState<AccountTripUpdate['visibility']>(
    trip.visibility === 'private' || trip.visibility === 'public' ? trip.visibility : 'pals',
  )
  const [pending, setPending] = useState<'save' | 'end' | 'delete' | null>(null)
  const [confirmEnd, setConfirmEnd] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string>()

  const save = async (event: FormEvent) => {
    event.preventDefault()
    setPending('save')
    setError(undefined)
    try {
      await onSave({ description: description.trim(), visibility })
      onClose()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The trip could not be updated.')
      setPending(null)
    }
  }

  const remove = async () => {
    setPending('delete')
    setError(undefined)
    try {
      await onDelete()
      onClose()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The trip could not be deleted.')
      setPending(null)
    }
  }

  const end = async () => {
    if (!onEnd) return
    setPending('end')
    setError(undefined)
    try {
      await onEnd()
      onClose()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The trip could not be ended.')
      setPending(null)
    }
  }

  return (
    <div className="settings-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !pending) onClose()
    }}>
      <section className="settings-dialog trip-dialog" role="dialog" aria-modal="true" aria-labelledby="edit-trip-title">
        <div className="settings-dialog__heading">
          <div><p>Your trip</p><h2 id="edit-trip-title">Edit trip</h2></div>
          <button type="button" aria-label="Close edit trip" onClick={onClose} disabled={pending !== null}>×</button>
        </div>
        <form onSubmit={save}>
          <label className="trip-dialog__field">
            <span>Description</span>
            <textarea value={description} maxLength={500} rows={3} onChange={(event) => setDescription(event.currentTarget.value)} />
          </label>
          <fieldset>
            <legend>Sharing</legend>
            {([
              ['private', 'Private', 'Only people with the private link'],
              ['pals', 'Pals', 'Shared with your Mapapal pals'],
              ['public', 'Public', 'Visible to everyone'],
            ] as const).map(([value, label, detail]) => (
              <label className={visibility === value ? 'settings-option settings-option--selected' : 'settings-option'} key={value}>
                <input type="radio" name="trip-visibility" value={value} checked={visibility === value} onChange={() => setVisibility(value)} />
                <span><strong>{label}</strong><small>{detail}</small></span>
              </label>
            ))}
          </fieldset>
          {error ? <p className="trip-dialog__error" role="alert">{error}</p> : null}
          <button className="settings-dialog__done" type="submit" disabled={pending !== null}>
            {pending === 'save' ? 'Saving…' : 'Save changes'}
          </button>
        </form>
        {onEnd ? (
          <div className="trip-dialog__end">
            {confirmEnd ? (
              <div role="alert">
                <p><strong>End this trip now?</strong><span>New positions will no longer be added to it.</span></p>
                <div>
                  <button type="button" onClick={() => setConfirmEnd(false)} disabled={pending !== null}>Cancel</button>
                  <button className="trip-dialog__end-confirm" type="button" onClick={end} disabled={pending !== null}>
                    {pending === 'end' ? 'Ending…' : 'End trip'}
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmEnd(true)} disabled={pending !== null}>End trip</button>
            )}
          </div>
        ) : null}
        <div className="trip-dialog__danger">
          {confirmDelete ? (
            <div role="alert">
              <p><strong>Delete this trip permanently?</strong><span>This cannot be undone.</span></p>
              <div>
                <button type="button" onClick={() => setConfirmDelete(false)} disabled={pending !== null}>Cancel</button>
                <button className="trip-dialog__delete-confirm" type="button" onClick={remove} disabled={pending !== null}>
                  {pending === 'delete' ? 'Deleting…' : 'Delete trip'}
                </button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)} disabled={pending !== null}>Delete trip</button>
          )}
        </div>
      </section>
    </div>
  )
}
